import { Calendar } from "@tamagui/lucide-icons";
import { Timestamp } from "firebase/firestore";
import { observer } from "mobx-react-lite";
import React, { useMemo } from "react";
import type { ModalProps } from "react-native";
import {
  FormDataModal,
  FormSheetType,
  type FormValues,
} from "../../core/components/FormDataModal";
import { getDayJsDate } from "../../core/dayJs";
import type BaseModuleModel from "../core/BaseModuleModel";
import type { ModuleModelData, Snapshot } from "../core/BaseModuleModel";

type BaseModuleWithDates = BaseModuleModel<Snapshot, ModuleModelData<Snapshot>>;

export const DateStartDateEndModal: React.FC<
  ModalProps & {
    initialDateStart: Timestamp | null;
    initialDateEnd: Timestamp | null;
    onClose: () => void;
    module: BaseModuleWithDates;
    onSave: ({
      dateStart,
      dateEnd,
    }: {
      dateStart: Timestamp | null;
      dateEnd: Timestamp | null;
    }) => void;
  }
> = observer(
  ({ module, initialDateStart, initialDateEnd, onSave, ...props }) => {
    const onSaveAction = React.useCallback(
      async (data: FormValues) => {
        // Set only updated values
        onSave({
          dateStart: data.dateStart
            ? Timestamp.fromDate(new Date(data.dateStart as string))
            : null,
          dateEnd: data.dateEnd
            ? Timestamp.fromDate(new Date(data.dateEnd as string))
            : null,
        });
      },
      [onSave],
    );

    return (
      <FormDataModal
        {...props}
        title={"Set dates"}
        buttonSaveText={"Save event"}
        values={useMemo(
          () => ({
            dateStart:
              getDayJsDate(
                initialDateStart,
                module.plan.data.settings.dateFormat,
              ) ?? "",
            dateEnd:
              getDayJsDate(
                initialDateEnd,
                module.plan.data.settings.dateFormat,
              ) ?? "",
          }),
          [
            initialDateStart,
            initialDateEnd,
            module.plan.data.settings.dateFormat,
          ],
        )}
        form={[
          {
            formKey: "dateStart",
            label: "Date start",
            type: FormSheetType.Date,
            icon: Calendar,
            required: true,
            placeholder: "Set start date...",
            plan: module.plan, // for settings
          },
          {
            formKey: "dateEnd",
            label: "Date end",
            type: FormSheetType.Date,
            icon: Calendar,
            placeholder: "(Optional) Set end date...",
            plan: module.plan, // for settings
          },
        ]}
        onSave={onSaveAction}
      />
    );
  },
);
